import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AssistantMarkdown } from '../AssistantMarkdown'
import { ResultDataTable } from '../ResultDataTable'
import { TooltipIconButton } from '../TooltipIconButton'
import { VegaChart } from '../VegaChart'
import {
  contentsFromMessages,
  formatStatsValue,
  selectScalarMetrics,
  selectTabularData,
  selectUserFacingErrors,
} from '../utils/displayContent.utils'
import { AssistantTurnProps } from './AssistantTurn.model'
import styles from './AssistantTurn.module.scss'

export const AssistantTurn: FunctionComponent<AssistantTurnProps> = ({
  messages,
  compactChart = false,
  onChartExpand,
  onRetry,
}) => {
  const { texts, data, charts, errors } = useMemo(() => contentsFromMessages(messages), [messages])
  const [copied, setCopied] = useState(false)
  const turnRef = useRef<HTMLElement>(null)

  const copyText = useMemo(() => texts.map(({ content }) => content.text).join('\n\n'), [texts])

  // Copie le rendu (HTML + texte) plutôt que le markdown source, pour ne pas coller de `**`
  const handleCopy = useCallback(async () => {
    const blocks = Array.from(
      turnRef.current?.querySelectorAll<HTMLElement>(`.${styles.textBlock}`) ?? [],
    )
    const html = blocks.map((block) => block.innerHTML).join('')
    const plain = blocks.map((block) => block.innerText.trim()).join('\n\n')
    if (typeof ClipboardItem !== 'undefined') {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ])
    } else {
      await navigator.clipboard.writeText(plain)
    }
    setCopied(true)
  }, [])

  useEffect(() => {
    if (!copied) return
    const timeout = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timeout)
  }, [copied])

  const scalarMetrics = selectScalarMetrics(data)
  const tabularData = selectTabularData(data, charts.length > 0)
  const displayErrors = selectUserFacingErrors(errors)

  if (
    texts.length === 0 &&
    tabularData.length === 0 &&
    charts.length === 0 &&
    scalarMetrics.length === 0 &&
    displayErrors.length === 0
  ) {
    return null
  }

  return (
    <article ref={turnRef} className={styles.turn}>
      {scalarMetrics.length > 0 && (
        <div className={styles.metricsRow}>
          {scalarMetrics.map(({ key, content }) => (
            <div key={key} className={styles.metricCard}>
              <span className={styles.metricLabel}>{content.columns[0]?.label ?? 'Résultat'}</span>
              <span className={styles.metricValue}>
                {formatStatsValue(content.rows[0]?.[0] ?? null)}
              </span>
            </div>
          ))}
        </div>
      )}

      {charts.map(({ key, content }) => (
        <div key={key} className={styles.chartBlock}>
          <VegaChart
            vegaSpec={content.vegaSpec}
            title={content.title}
            compact={compactChart}
            onExpand={onChartExpand}
          />
        </div>
      ))}

      {tabularData.map(({ key, content }) => (
        <div key={key} className={styles.tableBlock}>
          <ResultDataTable content={content} />
        </div>
      ))}

      {texts.map(({ key, content }) => (
        <div key={key} className={styles.textBlock}>
          <AssistantMarkdown text={content.text} />
        </div>
      ))}

      {displayErrors.map(({ key, content }) => (
        <div key={key} className={styles.errorBlock}>
          {content.userMessage}
        </div>
      ))}

      {(copyText || onRetry) && (
        <div className={styles.actions}>
          {copyText && (
            <TooltipIconButton
              icon={copied ? 'check' : 'copy'}
              label={copied ? 'Copié' : 'Copier la réponse'}
              onClick={() => void handleCopy()}
              testId="button-stats_assistant-copy_turn"
            />
          )}
          {onRetry && (
            <TooltipIconButton
              icon="reload"
              label="Régénérer la réponse"
              onClick={onRetry}
              testId="button-stats_assistant-retry_turn"
            />
          )}
        </div>
      )}
    </article>
  )
}
